import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, Chip, DChip, Icon, Pane, PaneHead } from '@/components/kit';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { allergenOf } from '@/utils/allergyText';
import { captureFile, useMyDay, useNurseTask, useTaskActions, type NurseTask } from '../api';
import { dayRows, paneOverline, rowTitle, LANE_CHIP, type DayFilter, type DayItem } from '../dayModel';
import {
  cantLabel, clock, dayMonth, drName, kindLabel, mmss, secondsLeft, shortCondition, shortName, sinceWhen, todayYmd,
  useNow,
} from '../format';
import { CantModal, DayCantSub } from './CantModal';
import { ReopenModal, TimerModal } from './NurseModal';

const isOpen = (t: NurseTask) => t.state === 'open' || t.state === 'in_progress';

/** "Hoda Samir" → "Hoda S." — how the note signs her work. */
function initialled(name: string | undefined): string {
  const [first, ...rest] = (name ?? '').trim().split(/\s+/);
  const last = rest.pop();
  return last ? `${first} ${last[0]}.` : first ?? '';
}

/** "She" / "He" from the chart — never guessed. */
function pronoun(t: NurseTask, capital = true): string {
  const w = t.patient?.sex === 'F' ? 'she' : t.patient?.sex === 'M' ? 'he' : 'the patient';
  return capital ? w[0].toUpperCase() + w.slice(1) : w;
}
function objectPronoun(t: NurseTask): string {
  return t.patient?.sex === 'F' ? 'her' : t.patient?.sex === 'M' ? 'him' : 'the patient';
}
function possessive(t: NurseTask): string {
  return t.patient?.sex === 'F' ? 'her' : t.patient?.sex === 'M' ? 'his' : 'the';
}

/** "IM injection · Omar K." / "Consent · minor procedure" / "Chaperone · Room 2". */
function paneTitle(t: NurseTask): string {
  if (t.title.includes(' — ')) return t.title.replace(' — ', ' · ');
  const who = t.patient ? shortName(t.patient) : t.room ?? '';
  return who ? `${t.title} · ${who}` : t.title;
}

/** "IM injection" — the route from the signed order, then the kind. */
function subLabel(s: NurseTask): string {
  const route = (s.orderText ?? '').match(/\b(IM|SC|IV)\b/i)?.[1]?.toUpperCase();
  return s.kind === 'injection' && route ? `${route} injection` : kindLabel(s);
}

/** "7:38" today, "24 Aug" on another day. */
function whenSigned(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return ymd === todayYmd() ? clock(iso) : dayMonth(ymd);
}

function TimerBox({ label, value, cap }: { label: string; value: string; cap: ReactNode }) {
  return (
    <div style={{ background: 'var(--brand-100)', border: '1px solid var(--brand-300)',
      borderRadius: 'var(--r-md)', padding: 16, textAlign: 'center' }}>
      <p className="t-ovl mut">{label}</p>
      <p style={{ fontSize: 30, lineHeight: '38px', fontWeight: 600, color: 'var(--brand-600)', margin: '4px 0' }}>
        {value}
      </p>
      <p className="t-cap mut">{cap}</p>
    </div>
  );
}

function Btn({ label, onClick, kind = 'primary', style }: {
  label: ReactNode;
  onClick: () => void;
  kind?: 'primary' | 'outline' | 'plain';
  style?: CSSProperties;
}) {
  const cls = kind === 'primary' ? 'btn btn-primary grow' : kind === 'outline' ? 'btn btn-outline grow' : 'btn grow';
  return (
    <button type="button" className={cls} style={{ minHeight: 44, ...style }} onClick={onClick}>{label}</button>
  );
}

/**
 * THE PANE IS THE SELECTED TASK (Law 4) — g_day's right column, drawn PER
 * KIND the way the canvas draws each row's pane (`tabOn.day.r1…r10`): a
 * dilation ordered mid-visit, one on its clock, a room's pair, vitals, an
 * injection course, an observation, a consent, a closed task, a chaperone, a
 * nurse-only visit.
 *
 * It reads the day itself rather than taking the task as a prop: the shell
 * re-publishes the pane only when the page's deps change, and a running clock
 * or a state flip must not leave a stale copy docked here. It rebuilds the
 * SAME rows the table shows, so it never docks a row the table is not showing.
 *
 * Every sentence the canvas states that the system does not do yet carries a
 * `not built` mark (reports/nurse.md, BACKEND-GAPs).
 */
export function TaskPane({ itemId, filter }: { itemId: string | null; filter: DayFilter }) {
  const day = useMyDay();
  const items = dayRows(day.data?.tasks ?? [], filter, '');
  const item: DayItem | null = items.find((i) => i.id === itemId) ?? items[0] ?? null;
  /* In a room's pair, a card opens that one task here; "‹ Both" goes back. */
  const [subId, setSubId] = useState<string | null>(null);
  const itemKey = item?.id ?? null;
  useEffect(() => setSubId(null), [itemKey]);
  const sub = item?.batch && subId ? item.tasks.find((s) => s.id === subId) ?? null : null;
  const t: NurseTask | null = sub ?? item?.primary ?? null;
  const running = Boolean(item?.tasks.some((x) => x.state === 'in_progress' && x.dueAt));
  const now = useNow(running);
  const navigate = useNavigate();
  const { person } = useShellIdentity();
  const act = useTaskActions();
  const [cantOpen, setCantOpen] = useState(false);
  const [timerOpen, setTimerOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  /* An injection's pane shows its course — every dose of the signed order. */
  const courseQ = useNurseTask(t && t.kind === 'injection' && t.courseId ? t.id : undefined);

  if (!item || !t) {
    return (
      <Pane>
        <div className="emptystate">
          <p className="t-sec">
            {filter === 'handed'
              ? 'Nothing is handed over, so nothing docks here. This panel is always an extension of the row set the table is showing.'
              : 'Nothing to dock — this panel follows the task you pick.'}
          </p>
        </div>
      </Pane>
    );
  }

  const req = drName(t.requestedBy);
  const doctor = req || 'the doctor';
  const left = secondsLeft(t, now);
  const me = initialled(person?.name);
  const fail = (e: unknown) => toast.error((e as Error).message || 'That did not go through');
  const finish = (id: string, outcome: string, msg: string) =>
    act.complete.mutate({ id, outcome }, { onSuccess: () => toast.success(msg), onError: fail });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      await captureFile(t.id, file);
      toast.success('Signed page filed on the patient’s documents — the consent task is closed');
      void day.refetch();
    } catch (e) {
      fail(e);
    }
  };

  /* What the note gets — composed from the task; kept on it, not written into the note yet. */
  const closedNow = !isOpen(t);
  const noteLine = (() => {
    const at = closedNow ? clock(t.completedAt, true)
      : t.startedAt ? clock(t.startedAt, true) : clock(new Date(now), true);
    const by = closedNow ? (t.completedBy ? shortName(t.completedBy) : '')
      : t.startedBy ? shortName(t.startedBy) : me;
    const verb = t.kind === 'dilate' || t.kind === 'injection' ? 'given by' : 'by';
    const sig = [at, by ? `${verb} ${by}` : ''].filter(Boolean).join(' · ');
    if (t.kind === 'dilate' && t.orderText) return `Dilation: ${t.orderText.replace(/,?\s*both eyes/i, ' OU')} · ${sig}`;
    if (t.orderText) return `${kindLabel(t)}: ${t.orderText} · ${sig}`;
    return `${kindLabel(t)}${t.patient ? '' : ` — ${t.room ?? ''}`} · ${sig}`;
  })();
  const noteCard = (
    <div className="card">
      <p className="t-ovl mut">{closedNow ? 'What the task kept' : 'What the task keeps — automatically'}</p>
      <p className="t-sec sec mt4">“{noteLine}”</p>
      <p className="t-cap mut mt4">kept on the task, with the name and the time</p>
    </div>
  );
  const cantChip = (
    <p className="mt12">
      <DChip kind="warn" onClick={() => setCantOpen(true)}>Can’t — reason ▾</DChip>
    </p>
  );

  const allergies = (t.patient?.allergies ?? []).map((a) => allergenOf(a) || a);
  const allergyCard = !t.patient ? null : allergies.length === 0 ? (
    <div className="card"><p className="t-sec mut">Allergies: none recorded</p></div>
  ) : t.allergyCheck.state === 'conflict' ? (
    <div className="card" style={{ borderColor: 'var(--danger-600)' }}>
      <p className="t-sec red w6">Allergies: {allergies.join(', ')} — conflicts with this order</p>
    </div>
  ) : (
    <div className="card" style={{ borderColor: 'var(--success-600)' }}>
      <p className="t-sec grn">Allergies: {allergies.join(', ')} — no conflict ✓</p>
    </div>
  );

  const pairDone = item.tasks.filter((x) => !isOpen(x)).length;
  const pairLine = `${pairDone} of ${item.tasks.length} · back when ${item.tasks.length === 2 ? 'both' : 'all'} finish`;
  const back = sub ? (
    <p className="t-sec" style={{ marginBottom: 8 }}>
      <button type="button" className="lnk" onClick={() => setSubId(null)}>
        ‹ {item.tasks.length === 2 ? 'Both' : `All ${item.tasks.length}`} now-tasks
      </button>
    </p>
  ) : null;
  const workable = isOpen(t) && !t.handedByMe;

  let overline = sub ? `${LANE_CHIP[item.lane].label} · parallel` : paneOverline(item);
  let title = item.batch && !sub ? rowTitle(item) : paneTitle(t);
  let subLine = '';
  let body: ReactNode = null;
  let foot: ReactNode = undefined;

  if (t.handedByMe) {
    /* HANDED OVER — hers no longer; the clock travelled with it. */
    subLine = [req, `handed ${clock(t.handedAt)}`].filter(Boolean).join(' · ');
    body = (
      <div className="card">
        <p className="t-ovl mut">Handed over</p>
        <p className="t-sec sec mt4">
          Handed to {shortName(t.assignee)} at {clock(t.handedAt)} — a running clock travelled with it, with the original
          start and giver on the record.
        </p>
      </div>
    );
  } else if (!isOpen(t)) {
    /* CLOSED — who closed it, what the note got, and Reopen (say why). */
    overline = `${LANE_CHIP[item.lane].label} · closed`;
    subLine = [kindLabel(t).toLowerCase(), req].filter(Boolean).join(' · ');
    const by = t.completedBy ? shortName(t.completedBy) : t.outcome === 'timer_elapsed' ? 'the timer ran out' : '';
    body = (
      <>
        {back}
        {t.state === 'cant' ? (
          <div className="card" style={{ borderColor: 'var(--warning-600)' }}>
            <p className="t-sec amb">
              Came back — {cantLabel(t.cantReason).toLowerCase()} · {clock(t.completedAt, true)}{by ? ` · ${by}` : ''}
            </p>
            {t.cantNote ? <p className="t-cap mut mt4">“{t.cantNote}”</p> : null}
          </div>
        ) : (
          <div className="card" style={{ borderColor: 'var(--success-600)' }}>
            <p className="t-sec grn">Done ✓ {clock(t.completedAt, true)}{by ? ` · ${by}` : ''}</p>
          </div>
        )}
        {t.state === 'done' ? noteCard : null}
      </>
    );
    foot = <Btn kind="outline" label="Reopen — say why" onClick={() => setReopenOpen(true)} />;
  } else if (item.batch && !sub) {
    /* A ROOM'S PAIR — one return, not two. A card opens that task here. */
    subLine = [req, 'one return, not two'].filter(Boolean).join(' · ');
    body = (
      <>
        {item.tasks.map((s) => (
          <Card key={s.id} onClick={() => setSubId(s.id)}>
            <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
              <p className="t-body grow">{subLabel(s)}</p>
              {s.state === 'done' ? (
                <Chip kind="success" small>{s.kind === 'injection' ? 'given' : 'done'} {clock(s.completedAt)} ✓</Chip>
              ) : s.state === 'cant' ? (
                <Chip kind="warn" small>came back</Chip>
              ) : s.state === 'in_progress' ? (
                <Chip kind="warn" small>in progress</Chip>
              ) : (
                <Chip kind="outline" small>waiting</Chip>
              )}
            </div>
          </Card>
        ))}
        <div className="card">
          <p className="t-sec sec">
            {pronoun(t)} returns to {doctor} when {item.tasks.length === 2 ? 'BOTH' : 'ALL'} finish — the counter reads{' '}
            {pairDone} of {item.tasks.length} until then.
          </p>
        </div>
      </>
    );
    const n = item.tasks.length;
    foot = (
      <Btn kind="outline" label={`What happens at ${n} of ${n}`}
        onClick={() => toast.info(
          `At ${n} of ${n} ${pronoun(t, false)} goes back to ${doctor} once — one return, not two. ${doctor} is notified when the last one closes.`,
        )} />
    );
  } else if (t.kind === 'dilate' && t.state === 'in_progress') {
    /* A DILATION ON ITS CLOCK. */
    title = `${shortName(t.patient)} — dilating`;
    subLine = `started ${clock(t.startedAt)} · back to ${doctor} at 0`;
    body = (
      <>
        <TimerBox label="Drops given — timer" value={left !== null ? mmss(left) : mmss(t.timerSeconds ?? 0)}
          cap={`running · started ${clock(t.startedAt)}${t.startedBy ? ` by ${shortName(t.startedBy)}` : ''}`} />
        <div className="card" style={{ marginTop: 8 }}>
          <p className="t-sec sec">
            The clock runs here; at 0 {pronoun(t).toLowerCase()} is ready to go back. {doctor} is not interrupted.
          </p>
        </div>
        {noteCard}
      </>
    );
    foot = (
      <Btn kind="outline" label={`Back to ${doctor} now`}
        onClick={() => finish(t.id, 'returned', `Back to ${doctor} now — the timer closes with your line on the task`)} />
    );
  } else if (t.kind === 'dilate') {
    /* A DILATION, ORDERED MID-VISIT — the drops not given yet. */
    subLine = [req, sinceWhen(t.orderSignedAt ?? t.createdAt, now), 'ordered mid-visit'].filter(Boolean).join(' · ');
    body = (
      <>
        {t.orderText ? (
          <div className="card card-acc">
            <p className="t-sec" style={{ color: 'var(--brand-600)' }}>
              <span className="w6">Ordered by {req || 'the room'} — mid-visit</span> · protocol: {t.orderText}
            </p>
          </div>
        ) : null}
        {allergyCard}
        {t.timerSeconds ? (
          <TimerBox label="Drops given — timer" value={mmss(t.timerSeconds)}
            cap={t.defaultTimerSeconds && t.defaultTimerSeconds !== t.timerSeconds
              ? `changed from the ${t.flow ? 'flow' : 'order'}’s ${mmss(t.defaultTimerSeconds)} — audited on the task · auto-returns ${objectPronoun(t)} to the READY lane at 0`
              : `default from the ${t.flow ? 'flow' : 'order'} · auto-returns ${objectPronoun(t)} to the READY lane at 0`} />
        ) : null}
        {t.flow ? (
          <div className="card" style={{ borderColor: 'var(--info-600)', marginTop: 8 }}>
            <p className="t-sec" style={{ color: 'var(--info-600)' }}>
              <span className="w6">Step {t.flow.step} of {t.flow.steps}</span> — at ⏱ 0 the {t.flow.next} unlocks on the
              technician’s worklist; the patient returns when all now-steps finish
            </p>
          </div>
        ) : null}
        {noteCard}
        {cantChip}
      </>
    );
    foot = (
      <>
        <button type="button" className="btn btn-outline" style={{ minHeight: 44 }} onClick={() => setTimerOpen(true)}>
          change ▾
        </button>
        <Btn label="Given — start timer" onClick={() => act.start.mutate(t.id, {
          onSuccess: () => toast.success(`Given — the timer starts; ${pronoun(t, false)} returns to ${doctor} at 0`),
          onError: fail,
        })} />
      </>
    );
  } else if (t.kind === 'vitals') {
    /* PREP — VITALS BEFORE THE DOCTOR. */
    subLine = t.appointment
      ? `#${t.appointment.number} · ${t.appointment.status === 'in-progress' ? 'in the room' : 'waiting'}`
      : 'waiting';
    const band = (t.patient?.conditions ?? []).slice(0, 3).map(shortCondition)
      .map((c) => (c.length > 26 ? `${c.slice(0, 24).trimEnd()}…` : c)).join(' · ');
    body = (
      <>
        <div className="card" style={allergies.length ? { borderColor: 'var(--danger-600)' } : undefined}>
          <p className={allergies.length ? 't-sec red w6' : 't-sec mut'}>
            {allergies.length ? `${allergies.join(', ')} allerg${allergies.length === 1 ? 'y' : 'ies'}` : 'No allergies recorded'}
          </p>
          <p className="t-cap mut mt4">{band ? `${band} · ` : ''}the band travels with every task</p>
        </div>
        <div className="card">
          <p className="t-ovl mut">Seven fields plus a note</p>
          <p className="t-sec sec mt4">
            BP · HR · Temp · Weight · RBS · SpO₂ · IOP — typed with Tab, a whole round without lifting a hand.
          </p>
        </div>
        <div className="card">
          <p className="t-sec sec">
            A critical value saves and pings the doctor now.
          </p>
        </div>
      </>
    );
    foot = <Btn label="Take vitals ▸" onClick={() => navigate(`/nurse/day/vitals/${t.id}`)} />;
  } else if (t.kind === 'injection') {
    /* AFTER THE VISIT — A SIGNED PLAN. She executes, never prescribes. */
    const dose = t.doseNo && t.doseTotal ? `dose ${t.doseNo} of ${t.doseTotal}` : '';
    subLine = sub ? pairLine
      : [req, t.orderSignedAt ? `signed ${whenSigned(t.orderSignedAt)}` : '', dose].filter(Boolean).join(' · ');
    const course = courseQ.data?.course ?? [];
    const idx = course.findIndex((d) => d.id === t.id);
    const shown = idx >= 0 ? course.slice(Math.max(0, idx - 1), idx + 2) : [];
    const today = todayYmd();
    body = (
      <>
        {back}
        {t.orderText ? (
          <div className="card card-acc">
            <p className="t-sec" style={{ color: 'var(--brand-600)' }}>
              <span className="w6">Signed order — {doctor}:</span> {t.orderText}
            </p>
          </div>
        ) : null}
        {t.allergyCheck.state === 'conflict' ? allergyCard : null}
        {shown.length > 1 ? (
          <div className="card">
            <p className="t-ovl mut">The course</p>
            <p className="t-sec sec mt4">
              {shown.map((d, i) => {
                const ymd = d.scheduledFor ?? d.createdAt.slice(0, 10);
                const when = ymd === today ? 'today' : dayMonth(ymd);
                const state = d.id === t.id ? '' : d.state === 'done' ? ' given' : d.state === 'cant' ? ' missed' : ' scheduled';
                const text = `Dose ${d.doseNo} · ${when}${state}`;
                return (
                  <span key={d.id}>
                    {i ? ' · ' : null}
                    {d.id === t.id ? <span className="w6 ink">{text}</span> : text}
                  </span>
                );
              })}
            </p>
          </div>
        ) : null}
        <div className="card">
          <p className="t-sec sec">The nurse executes, never prescribes: drug, dose and route come from the signed order.</p>
        </div>
        {cantChip}
      </>
    );
    foot = <Btn label="Open the injection ▸" onClick={() => navigate(`/nurse/day/task/${t.id}`)} />;
  } else if (t.kind === 'observe') {
    /* AN OBSERVATION — two exits, and only two. */
    subLine = [t.detail?.startsWith('post-injection') ? 'post-injection' : 'observation',
      t.startedAt ? `started ${clock(t.startedAt)}` : ''].filter(Boolean).join(' · ');
    body = (
      <>
        {t.timerSeconds ? (
          <TimerBox label="Observation — timer"
            value={t.state === 'in_progress' && left !== null ? mmss(left) : mmss(t.timerSeconds)}
            cap={`${t.state === 'in_progress' ? 'running · ' : ''}the clock keeps its own time, and travels with the task`} />
        ) : null}
        <div className="card" style={{ marginTop: 8 }}>
          <p className="t-sec sec">Two exits, and only two: {pronoun(t, false)} left well, or the doctor is called now.</p>
        </div>
      </>
    );
    foot = (
      <>
        <Btn kind="outline" label="⚠ Not well" style={{ borderColor: 'var(--danger-600)', color: 'var(--danger-600)' }}
          onClick={() => finish(t.id, 'not_well', `⚠ Not well — ${doctor} is called now`)} />
        <Btn kind="plain" label="Left well ✓" style={{ background: 'var(--success-600)', color: 'var(--surface-0)' }}
          onClick={() => finish(t.id, 'left_well', 'Left well ✓ — the observation closes')} />
      </>
    );
  } else if (t.kind === 'consent') {
    /* A CONSENT THAT BLOCKS THE ORDER. */
    subLine = [shortName(t.patient), 'blocks the order'].filter(Boolean).join(' · ');
    body = (
      <>
        <div className="card" style={{ borderColor: 'var(--warning-600)' }}>
          <p className="t-sec amb">
            The signed page goes on the record before the procedure.
          </p>
        </div>
        <div className="card">
          <p className="t-ovl mut">What to scan</p>
          <p className="t-sec sec mt4">
            The signed consent page. It is filed on the patient’s documents.
          </p>
        </div>
      </>
    );
    foot = <Btn label={<><Icon name="camera" /> Scan signed</>} onClick={() => fileRef.current?.click()} />;
  } else if (t.kind === 'chaperone') {
    /* PRESENCE — the stamp is the record. */
    const since = sinceWhen(t.createdAt, now);
    subLine = [req ? `${req} requested` : '', since === 'just now' ? 'now' : since].filter(Boolean).join(' · ');
    body = (
      <div className="card">
        <p className="t-sec sec">
          Your presence is the record: stamping it keeps your name and the time on the task.
        </p>
      </div>
    );
    foot = (
      <Btn label="Stamp presence"
        onClick={() => finish(t.id, 'present', 'Presence stamped — kept with your name and the time')} />
    );
  } else if (t.nurseOnly) {
    /* A NURSE-ONLY VISIT — no consultation. "Do" finishes it. */
    subLine = 'walk-in · no consultation';
    body = (
      <>
        <div className="card" style={{ borderColor: 'var(--warning-600)' }}>
          <p className="t-sec amb">
            Nurse-only visit — no consultation, on {possessive(t)} walk-in.
          </p>
        </div>
      </>
    );
    foot = <Btn label="Do ▸" onClick={() => finish(t.id, 'done', 'Done ✓ — kept on the task with your name')} />;
  } else {
    /* ANYTHING ELSE THE ROOM ASKED FOR — a dressing, a line of teaching. */
    subLine = sub ? pairLine : [req, sinceWhen(t.orderSignedAt ?? t.createdAt, now)].filter(Boolean).join(' · ');
    body = (
      <>
        {back}
        {t.orderText ? (
          <div className="card card-acc">
            <p className="t-sec" style={{ color: 'var(--brand-600)' }}>
              <span className="w6">Asked by {doctor}:</span> {t.orderText}
            </p>
          </div>
        ) : null}
        {allergyCard}
        {noteCard}
        {cantChip}
      </>
    );
    foot = (
      <Btn label={t.state === 'in_progress' ? 'Done ✓' : 'Do ▸'}
        onClick={() => finish(t.id, 'done', 'Done ✓ — kept on the task with your name')} />
    );
  }

  return (
    <Pane head={<PaneHead overline={overline} title={title} sub={subLine} />} foot={foot}>
      <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" hidden
        onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
      {body}

      {workable ? (
        <CantModal task={t} open={cantOpen} onClose={() => setCantOpen(false)} onDone={() => setCantOpen(false)}
          sub={<DayCantSub />} />
      ) : null}
      <TimerModal
        open={timerOpen}
        onClose={() => setTimerOpen(false)}
        current={t.timerSeconds}
        defaultSeconds={t.defaultTimerSeconds}
        flowName={t.flow?.name ?? null}
        busy={act.setTimer.isPending}
        onSet={(seconds, note) =>
          act.setTimer.mutate(
            { id: t.id, seconds, note },
            {
              onSuccess: () => {
                toast.success(`Timer set to ${mmss(seconds)} — audited on the task: who · from → to`);
                setTimerOpen(false);
              },
              onError: fail,
            },
          )}
      />
      <ReopenModal
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        title={paneTitle(t)}
        busy={act.reopen.isPending}
        onReopen={(reason) =>
          act.reopen.mutate(
            { id: t.id, reason },
            {
              onSuccess: () => {
                toast.success('Reopened — your reason is on the record beside the line that closed it');
                setReopenOpen(false);
              },
              onError: fail,
            },
          )}
      />
    </Pane>
  );
}
